import type { Invoice } from "@freightsimple/generated-dashboard-openapi-client/models/Invoice";
import { InvoiceState } from "@freightsimple/generated-dashboard-openapi-client/models/InvoiceState";
import { InvoiceType } from "@freightsimple/generated-dashboard-openapi-client/models/InvoiceType";
import { sumOf } from "@/Helpers/sumOf";

// Refunds come off the total; every other invoice type adds to it.
//
// Exported because every money total on the Invoices screen needs it, not just this one: the
// tiles above the month list (Total open, Overdue, In dispute, Payment Sent) each sum a
// different slice of the same invoices and must apply the same rule, or the top of the screen
// contradicts the headers below it (ENG-7104).
export function invoicesSignFactor(invoice: Invoice): number {
  return invoice.invoiceType === InvoiceType.ShipmentRefund ||
    invoice.invoiceType === InvoiceType.AdditionalChargeRefund
    ? -1
    : 1;
}

/**
 * What a set of invoices actually cost the customer.
 *
 * A voided invoice was cancelled, so the customer never owed it and it doesn't
 * count here — the tables still list it so the history stays visible. Drop the
 * voided ones first and flip the sign second: a voided refund that kept its -1
 * and then wasn't excluded would add its amount back on as a positive.
 *
 * Shared by the shipment page's Billing TOTAL and the Invoices screen's monthly
 * Total Spend, which are the same question asked of two different invoice
 * lists. They were worked out separately once and disagreed (ENG-7104), which
 * is why this lives in Exports rather than beside the screen that uses it most.
 */
export function invoicesTotalSpend(invoices: Invoice[]): number {
  return sumOf(
    invoices.filter((invoice) => invoice.invoiceState !== InvoiceState.Voided),
    (invoice) => invoicesSignFactor(invoice) * invoice.amount,
  );
}
